import { useUserSession } from '@solinkcorp/authentication';
import { useSegmentCache } from 'components/CacheContext';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import { useEffect, useMemo, useState } from 'react';
import { useDevicesAreLocal } from 'src/hooks/useDeviceIsLocal';
import { useMemoShallowEquals } from 'src/hooks/useMemoShallowEquals';
import { usePlayListSource } from 'src/hooks/usePlayListSource';
import { type SegmentCacheResult } from 'utils/PlaylistCache/SegmentCache';
import { uniq } from 'utils/utils';
import { type MotionSegment, type MotionSegmentsByCamera } from '../types';

const NO_SEGMENTS: SegmentCacheResult = Object.freeze({});

/**
 * This loads cached playlists for the specified interval.
 *
 * The return value is a SegmentData[], with one entry for each segment pulled
 * from the cache - when moving across days, you'll get back two values in this
 * array, one for each day.  The idea here is to make it easier for consumers
 * to know what they do and don't have to update - when you move from being acros
 * two days to just being on one day, one of the items will disappear and the other
 * will remain, which means somewhere in a React component one entire child tree
 * will be removed, but the other won't need to be updated at all.
 */
export function useMotionSegments(params: {
  /** Cameras to load playlists for.  If this is undefined, no playlists will be loaded. */
  playingCameras: PlayingCamera[];
  /** If true, playlists will only be loaded from the cache. */
  ifCached?: boolean;
  start: number;
  end: number;
}): [SegmentCacheResult, Error | undefined] {
  const segmentCache = useSegmentCache();
  const [error, setError] = useState<Error | undefined>();
  const [cachedSegments, setCachedSegments] = useState<SegmentCacheResult | undefined>();

  const playingLocationIds = useMemo(
    () => uniq(params.playingCameras.map((playingCamera) => playingCamera.camera.deviceId)),
    [params.playingCameras],
  );
  const localLocations = useDevicesAreLocal(playingLocationIds);

  const user = useUserSession();

  const playlistSource = usePlayListSource();

  const query = useMemoShallowEquals({
    user,
    playingCameras: params.playingCameras,
    start: params.start,
    end: params.end,
    selectedPlayListSource: playlistSource,
    localLocations,
  });

  useEffect(() => {
    const unsubscribe = segmentCache.subscribe(query, (err, segments) => {
      if (err) {
        setError(err);
        setCachedSegments(undefined);
      } else {
        setError(undefined);
        setCachedSegments(segments);
      }
    });
    return unsubscribe;
  }, [segmentCache, query]);

  return [cachedSegments || NO_SEGMENTS, error];
}

/**
 * Retrieve all motion segments for a given playerId.
 */
export function consolidateMotionSegments<T extends MotionSegment = MotionSegment>(
  motionSegments: MotionSegmentsByCamera<T>,
  locationId: string,
  vmsCameraId: string,
): T[] {
  let result: T[] = [];

  const cacheEntries = motionSegments[locationId]?.[vmsCameraId];
  if (cacheEntries) {
    for (const cacheEntry of cacheEntries) {
      result = result.concat(cacheEntry.segments);
    }
  }

  return result;
}
